import { useEffect, useState } from "react";
import { CardOrder } from "ts/api/order";
import { ExtraProduct, FullProduct } from "ts/api/product";
import composeCardOrder from "view/apps/Exhibitor/pages/Cart/helpers/composeOrderFileds/composers/composeCardOrder";
import { PaymentMethodType } from "ts/api/paymentMethod";
import { useShowDiscountCodes } from "store/discount-code/discount-code-store";
import { useSelector } from "react-redux";
import { selectTotalAmount } from "store/exhibitors/cartStepper/slice";

import { StepOrder } from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/OrdersList";
import { IOrganiser, IShow } from "@evennt-tech/api-v2-core";

type Params = {
  show: IShow;
  stepperState: StepOrder[];
  organiser: IOrganiser;
  transferredPaymentDescription: string;
  products: FullProduct[];
  exhibitorPairId: string;
};

export const useMultiStepOrders = ({
  show,
  stepperState,
  organiser,
  transferredPaymentDescription,
  products,
  exhibitorPairId,
}: Params) => {
  const appliedDiscountCodes = useShowDiscountCodes(show.id);
  const [multiStepOrders, setMultiStepOrders] = useState<CardOrder[]>([]);
  const totalAmount = useSelector(selectTotalAmount);

  useEffect(() => {
    if (!multiStepOrders.length) return;

    const updateOrders = async () => {
      const updated = await Promise.all(
        multiStepOrders.map(async (order) => {
          const orderState = stepperState.find((o) => o.id === order.id);

          if (!orderState || orderState.isPaid) {
            return order;
          }

          const supplierCompanyId = order.products?.[0]?.owner?.id;
          const amount = order.review
            ? totalAmount.withApproval?.[supplierCompanyId]
            : totalAmount.withoutApproval;

          if (amount === undefined || !supplierCompanyId) {
            return order;
          }

          const supplierProducts = order.products.reduce<FullProduct[]>(
            (acc, orderProduct) => {
              const fullProduct = products.find(
                (p) => p.id === orderProduct.id
              );
              if (!fullProduct) return acc;

              const extras = fullProduct.extras?.reduce<ExtraProduct[]>(
                (extrasAcc, extra) => {
                  const orderExtra = orderProduct.extras?.find(
                    (e) => e.id === extra.id
                  );
                  if (orderExtra) {
                    extrasAcc.push({ ...extra, quantity: orderExtra.quantity });
                  }
                  return extrasAcc;
                },
                []
              );

              acc.push({
                ...fullProduct,
                cartQuantity: orderProduct.quantity,
                extras,
              });

              return acc;
            },
            []
          );

          const newOrder = await composeCardOrder({
            paymentType: PaymentMethodType.cardPayment,
            id: order.id,
            isRequiredApproval: !!order.review,
            createdAt: order.createdAt,
            billingDetails: order.billingDetails,
            exhibitorId: order.exhibitorId,
            standId: order.standId,
            show,
            exhibitorPairId,
            organiser,
            totalAmount: amount,
            transferredPaymentDescription,
            products: supplierProducts,
            orderGroupId: order.orderGroupId,
          });

          newOrder.paymentDetails = {
            ...newOrder.paymentDetails,
            status: order.paymentDetails.status,
          };

          return newOrder;
        })
      );

      setMultiStepOrders(updated);
    };

    updateOrders();
  }, [appliedDiscountCodes, stepperState, products, totalAmount]);

  return [multiStepOrders, setMultiStepOrders] as const;
};
